/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    ActionGroup,
    ActionTagApi,
    Configuration,
    ConfigurationParameters,
    ContentScreenDetails,
    ContentScreenDetailsContentGetV2Request,
    ContentScreenDetailsVodOfferGetV2Request,
    OfferActionsGetV2Request,
    RecordingActionsGetV2Request,
    UnifiedCollectionScreenDetailsGetV2Request,
    VodOfferActionsGetV2Request,
} from 'client-code/actions';
import { ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IActionsServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { ActionsServiceV2Contract } from '../servicecontracts';
import {
    TActionList,
    TActionsContentParams,
    TActionsContentScreenDetailsContentIdParamsV2,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParams,
    TActionsParamsUCScreenDetails,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TCustomActionList,
    TPlayNextContentActionsParams,
    TPlayNextRecordingActionsParams,
    TServicesParams,
    TUnifiedCollectionScreenDetails,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class ActionsServiceRepositoryImplV2
    implements IActionsServiceRepository<ActionsServiceV2Contract>
{
    private static TAG: string = 'ActionsServiceRepositoryImplV2';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders) {
        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
        return configurationParameters;
    }

    private getRequestParameters(commonHeaders, groups, channelNumber) {
        return {
            applicationName: commonHeaders.ApplicationName,
            channelNumber,
            deviceType: commonHeaders.DeviceType,
            groups,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            timeZoneName: this.mParams.timeZoneName,
            bodyId: this.mParams.bodyId,
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getContentScreenDetailsContentActionsFromServiceV2(
        actionsParams: TActionsContentScreenDetailsContentIdParamsV2,
    ): Promise<ContentScreenDetails> {
        try {
            const {
                contentId,
                screenType,
                groups,
                channelNumber,
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            } = actionsParams;
            const commonHeaders = new CommonHeaders(
                screenType || APPLICATION_FEATURE_AREA.PROGRAM_SCREEN,
            ).headers;
            const requestContentScreenDetailsParameters: ContentScreenDetailsContentGetV2Request = {
                contentId,
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };
            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const contentScreenDetailsContentIdResponse =
                await actionsApi.contentScreenDetailsContentGetV2(
                    requestContentScreenDetailsParameters,
                );
            return contentScreenDetailsContentIdResponse.data as ContentScreenDetails;
        } catch (error) {
            Log.e(
                ActionsServiceRepositoryImplV2.TAG,
                'getContentScreenDetailsContentActionsFromServiceV2()',
                error,
            );
            throw error;
        }
    }

    private async getOfferActionsFromServiceV2(
        actionsParams: TActionsParams,
    ): Promise<TActionList> {
        try {
            const { offerId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;
            const requestParameters: OfferActionsGetV2Request = {
                offerId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };
            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const actionsApiResponseObj = await actionsApi.offerActionsGetV2(requestParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getOfferActionsFromServiceV2()', error);
            throw error;
        }
    }

    private async getRecordingActionsFromServiceV2(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        try {
            const { recordingId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const actionsApi = new ActionTagApi(
                new Configuration(this.getConfigurationParameters(commonHeaders)),
            );
            const reqParameters: RecordingActionsGetV2Request = {
                recordingId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };
            const actionsApiResponseObj = await actionsApi.recordingActionsGetV2(reqParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getRecordingActionsFromServiceV2()', error);
            throw error;
        }
    }

    private async getVodOfferActionsFromServiceV2(
        actionsParams: TActionsVodOfferParams,
    ): Promise<TActionList> {
        try {
            const { vodOfferId, groups, channelNumber, screenType, actionsNetworkCallId } =
                actionsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;
            const requestParameters: VodOfferActionsGetV2Request = {
                vodOfferId,
                ...this.getRequestParameters(commonHeaders, groups, channelNumber),
            };

            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const actionsApiResponseObj = await actionsApi.vodOfferActionsGetV2(requestParameters);
            const actionsApiResponse: TActionList = {
                actionsNetworkCallId,
                actionList: actionsApiResponseObj.data as TCustomActionList,
            };
            return actionsApiResponse;
        } catch (error: any) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'getVodOfferActionsFromServiceV2()', error);
            throw error;
        }
    }

    private async getUnifiedCollectionScreenDetailsActionsFromServiceV2(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        try {
            const { unifiedCollectionId, partnerId, contentSupplierPartnerId } = actionsParams;
            const commonHeaders = new CommonHeaders(ScreenType.SeriesScreen).headers;
            const requestClDetailsActionsParameters: UnifiedCollectionScreenDetailsGetV2Request = {
                unifiedCollectionId,
                partnerId,
                contentSupplierPartnerId,
                ...this.getRequestParameters(commonHeaders, null, null),
            };

            const actionsApi = new ActionTagApi(
                new Configuration(this.getConfigurationParameters(commonHeaders)),
            );
            const unifiedCollectionScreenDetailsResponse =
                await actionsApi.unifiedCollectionScreenDetailsGetV2(
                    requestClDetailsActionsParameters,
                );
            return unifiedCollectionScreenDetailsResponse.data as TUnifiedCollectionScreenDetails;
        } catch (error: any) {
            Log.e(
                ActionsServiceRepositoryImplV2.TAG,
                'getUnifiedCollectionScreenDetailsActionsFromServiceV2()',
                error,
            );
            throw error;
        }
    }

    public async fetchContentScreenDetailsVodOfferActions(actionParams: {
        vodOfferId: string;
        screenType: string;
        collapsedVodItemId?: string;
        networkCallId?: number;
        groups?: Array<ActionGroup>;
        channelNumber?: string;
    }): Promise<ContentScreenDetails> {
        try {
            const { vodOfferId, screenType, groups, channelNumber, collapsedVodItemId } =
                actionParams;
            const commonHeaders = new CommonHeaders(screenType).headers;
            const requestContentScreenDetailsParameters: ContentScreenDetailsVodOfferGetV2Request =
                {
                    vodOfferId,
                    collapsedVodItemId,
                    ...this.getRequestParameters(commonHeaders, groups, channelNumber),
                };
            const configuration = new Configuration(this.getConfigurationParameters(commonHeaders));
            const actionsApi = new ActionTagApi(configuration);
            const contentScreenDetailsContentIdResponse =
                await actionsApi.contentScreenDetailsVodOfferGetV2(
                    requestContentScreenDetailsParameters,
                );
            return contentScreenDetailsContentIdResponse.data as ContentScreenDetails;
        } catch (error) {
            Log.e(
                ActionsServiceRepositoryImplV2.TAG,
                'getContentScreenDetailsVodOfferActionsFromServiceV2()',
                error,
            );
            throw error;
        }
    }

    /* @inheritdoc
     */
    public async fetchContentScreenDetailsContentActions(
        actionsParams: TActionsContentScreenDetailsContentIdParamsV2,
    ): Promise<ContentScreenDetails> {
        try {
            return await this.getContentScreenDetailsContentActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ): Promise<ContentScreenDetails> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        try {
            return await this.getOfferActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingActions(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        try {
            return await this.getRecordingActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchUnifiedCollectionScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        try {
            return await this.getUnifiedCollectionScreenDetailsActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferActions(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        try {
            return await this.getVodOfferActionsFromServiceV2(actionsParams);
        } catch (error) {
            Log.e(ActionsServiceRepositoryImplV2.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchPlayNextContentActions(
        playNextContentActionsParams: TPlayNextContentActionsParams,
    ): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchPlayNextRecordingActions(
        playNextRecordingActionsParams: TPlayNextRecordingActionsParams,
    ): Promise<TActionList> {
        throw new Error('Method not implemented.');
    }
}
